import { isAccessTokenRejection } from "./authErrors";

/** Cookie-session recovery. Tokens never enter browser storage. */
export type SessionOutcome = "authenticated" | "session-ended" | "temporarily-unavailable";
export class SessionEndedError extends Error {
  constructor() {
    super("Your session has expired. Please log in again.");
    this.name = "SessionEndedError";
  }
}
export class SessionUnavailableError extends Error {
  constructor() {
    super("Unable to verify your session. Please try again.");
    this.name = "SessionUnavailableError";
  }
}

interface SessionTransportOptions {
  name: string;
  me: () => string;
  refresh: () => string;
  owns: (url: URL) => boolean;
  isPublic: (path: string, headers: Headers) => boolean;
  changesSession: (path: string) => boolean;
  endsSession?: (path: string) => boolean;
}

function requestOptions(input: RequestInfo | URL, init?: RequestInit) {
  const request = input instanceof Request ? input : undefined;
  return {
    href: input instanceof Request ? input.url : String(input),
    method: (init?.method ?? request?.method ?? "GET").toUpperCase(),
    signal: init?.signal ?? request?.signal,
    headers: new Headers(init?.headers ?? request?.headers),
  };
}

interface Recovery {
  expected: string;
  controller: AbortController;
  refreshStarted: boolean;
  consumers: number;
  promise: Promise<SessionOutcome>;
}

function waitForRecovery(job: Recovery, signal?: AbortSignal | null): Promise<SessionOutcome> {
  job.consumers++;
  return new Promise((resolve, reject) => {
    let finished = false;
    const finish = () => {
      if (finished) return false;
      finished = true;
      signal?.removeEventListener("abort", abort);
      job.consumers--;
      return true;
    };
    const abort = () => {
      if (!finish()) return;
      // A started rotation must finish receiving cookies, even if nobody waits.
      if (!job.consumers && !job.refreshStarted) job.controller.abort();
      reject(signal?.reason);
    };
    signal?.addEventListener("abort", abort, { once: true });
    job.promise.then(
      (outcome) => {
        if (finish()) resolve(outcome);
      },
      (error: unknown) => {
        if (finish()) reject(error);
      },
    );
    if (signal?.aborted) abort();
  });
}

export function createSessionTransport(options: SessionTransportOptions) {
  let inFlight: Recovery | undefined;
  let cooldown: { version: string; until: number } | undefined;
  let endedVersion: string | undefined;
  let localVersion = "initial";
  const versionKey = `${options.name}:session-version`;
  const cooldownKey = `${options.name}:session-cooldown`;
  const lockName = `${options.name}:session`;

  function version() {
    try {
      return localStorage.getItem(versionKey) ?? localVersion;
    } catch {
      return localVersion;
    }
  }
  function changed() {
    localVersion = globalThis.crypto.randomUUID();
    try {
      localStorage.setItem(versionKey, localVersion);
    } catch {
      /* Storage may be disabled. */
    }
    endedVersion = undefined;
    cooldown = undefined;
  }
  function locked<T>(fn: () => Promise<T>, signal?: AbortSignal): Promise<T> {
    if (typeof navigator !== "undefined" && navigator.locks) {
      return navigator.locks.request(lockName, { signal }, fn);
    }
    signal?.throwIfAborted();
    return fn();
  }
  function assertCurrent(expected: string, signal?: AbortSignal | null) {
    signal?.throwIfAborted();
    const current = version();
    if (current === `ended:${expected}` || current === `ended:logout:${expected}`) {
      ended(current);
      throw new SessionEndedError();
    }
    if (current !== expected) throw new DOMException("Session changed", "AbortError");
  }
  function ended(expected: string, logout = false): SessionOutcome {
    assertCurrent(expected);
    // Persist a terminal version so every tab rejects responses from the old
    // session, and observing an existing terminal version never advances it again.
    const prefix = logout ? "ended:logout:" : "ended:";
    const terminal = expected.startsWith("ended:") ? expected : `${prefix}${expected}`;
    localVersion = terminal;
    try {
      localStorage.setItem(versionKey, terminal);
    } catch {
      /* Disabled storage retains per-tab invalidation. */
    }
    cooldown = undefined;
    if (endedVersion !== terminal) {
      endedVersion = terminal;
      if (globalThis.window !== undefined)
        globalThis.dispatchEvent(
          new Event(
            `${options.name}:${terminal.startsWith("ended:logout:") ? "session-logout" : "session-ended"}`,
          ),
        );
    }
    return "session-ended";
  }
  function cooldownUntil(expected: string): number {
    const local = cooldown?.version === expected ? cooldown.until : 0;
    try {
      const stored: unknown = JSON.parse(localStorage.getItem(cooldownKey) ?? "null");
      if (
        typeof stored === "object" &&
        stored !== null &&
        "version" in stored &&
        stored.version === expected &&
        "until" in stored &&
        typeof stored.until === "number" &&
        Number.isFinite(stored.until)
      ) {
        return Math.max(local, stored.until);
      }
    } catch {
      /* Keep the per-tab fallback if storage is disabled or malformed. */
    }
    return local;
  }
  function unavailable(expected: string, response?: Response): SessionOutcome {
    const retryAfter = response?.headers?.get("Retry-After");
    const seconds = retryAfter ? Number(retryAfter) : Number.NaN;
    let delay = 0;
    if (Number.isFinite(seconds)) {
      delay = seconds * 1000;
    } else if (retryAfter) {
      delay = Date.parse(retryAfter) - Date.now();
    }
    cooldown = {
      version: expected,
      until: Math.max(
        cooldownUntil(expected),
        Date.now() + Math.max(1000, Number.isFinite(delay) ? delay : 0),
      ),
    };
    try {
      localStorage.setItem(cooldownKey, JSON.stringify(cooldown));
    } catch {
      /* The local cooldown still applies when storage is disabled. */
    }
    return "temporarily-unavailable";
  }
  const rawSession = (url: string, method = "GET", signal?: AbortSignal) =>
    fetch(url, {
      method,
      credentials: "include",
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal: signal
        ? AbortSignal.any([signal, AbortSignal.timeout(30_000)])
        : AbortSignal.timeout(30_000),
    });

  async function runRecovery(job: Recovery, expected: string): Promise<SessionOutcome> {
    const signal = job.controller.signal;
    assertCurrent(expected, signal);
    if (Date.now() < cooldownUntil(expected)) return "temporarily-unavailable";
    try {
      // A previous request/tab may already have replaced the cookies.
      const current = await rawSession(options.me(), "GET", signal);
      assertCurrent(expected, signal);
      if (current.ok) {
        endedVersion = undefined;
        return "authenticated";
      }
      if (current.status !== 401) return unavailable(expected, current);
      job.refreshStarted = true;
      const refreshed = await rawSession(options.refresh(), "POST");
      assertCurrent(expected);
      if (!refreshed.ok && refreshed.status !== 401) return unavailable(expected, refreshed);
      // Old clients/tabs may not participate in our lock. Verify before expiry.
      const verified = await rawSession(options.me());
      assertCurrent(expected);
      if (verified.ok) {
        endedVersion = undefined;
        return "authenticated";
      }
      return refreshed.status === 401 && verified.status === 401
        ? ended(expected)
        : unavailable(expected, verified);
    } catch {
      assertCurrent(expected, signal);
      return unavailable(expected);
    }
  }

  function recover(signal?: AbortSignal | null): Promise<SessionOutcome> {
    if (signal?.aborted) return Promise.reject(signal.reason);
    if (version().startsWith("ended:")) return Promise.resolve(ended(version()));
    if (inFlight?.expected !== version() || inFlight.controller.signal.aborted) {
      const expected = version();
      const job: Recovery = {
        expected,
        controller: new AbortController(),
        refreshStarted: false,
        consumers: 0,
        // Defer lock acquisition until the first consumer has subscribed.
        promise: Promise.resolve()
          .then(() => locked(() => runRecovery(job, expected), job.controller.signal))
          .finally(() => {
            if (inFlight === job) inFlight = undefined;
          }),
      };
      inFlight = job;
    }
    return waitForRecovery(inFlight, signal);
  }

  async function sessionFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const { href, method, signal, headers } = requestOptions(input, init);
    const url = new URL(
      href,
      typeof location === "undefined" ? "http://localhost" : location.origin,
    );
    if (!options.owns(url)) return fetch(input, init);
    const transition =
      options.changesSession(url.pathname) && method !== "GET" && method !== "HEAD";
    const publicRequest = options.isPublic(url.pathname, headers);
    let expected = version();
    const send = (request: RequestInfo | URL) => {
      if (!transition) return fetch(request, { ...init, credentials: "include" });
      return locked(async () => {
        const current = version();
        if (
          publicRequest &&
          (current === `ended:${expected}` || current === `ended:logout:${expected}`)
        )
          expected = current;
        assertCurrent(expected, signal);
        const logout = options.endsSession?.(url.pathname) === true;
        if (logout) {
          ended(expected, true);
          expected = version();
        }
        const response = await fetch(request, { ...init, credentials: "include" });
        if (!logout && response.ok) {
          assertCurrent(expected);
          changed();
          expected = version();
        }
        return response;
      }, signal ?? undefined);
    };
    if (publicRequest) return send(input);
    if (expected.startsWith("ended:") && !transition) {
      ended(expected);
      throw new SessionEndedError();
    }
    // Clone before fetch consumes a Request body. Explicit streaming bodies are
    // not replayable; JSON, FormData, Blob and ArrayBuffer callers retain theirs.
    const streaming = init?.body instanceof ReadableStream;
    const retryInput = input instanceof Request && !streaming ? input.clone() : input;
    const response = await send(input);
    assertCurrent(expected, signal);
    if (response.status !== 401) return response;
    const body: unknown = await response
      .clone()
      .json()
      .catch(() => null);
    const rejectedBeforeHandler = isAccessTokenRejection(body);
    const outcome = await recover(signal);
    assertCurrent(expected, signal);
    if (outcome === "temporarily-unavailable") throw new SessionUnavailableError();
    if (outcome === "session-ended") throw new SessionEndedError();
    const safe = method === "GET" || method === "HEAD";
    if ((!safe && !rejectedBeforeHandler) || streaming) return response;
    const retried = await send(retryInput);
    assertCurrent(expected, signal);
    // A second 401 is a request error, never a recursive refresh/logout trigger.
    return retried;
  }
  function subscribeToChanges(invalidate: () => void, reload: () => void) {
    let observed = version();
    let active = true;
    const inspect = () => {
      const current = version();
      if (current === observed) return;
      observed = current;
      if (current === localVersion) return; // This tab already owns its transition.
      if (current.startsWith("ended:")) {
        ended(current);
        return;
      }
      invalidate();
      // Wait for the other tab to receive cookies before reloading its identity.
      const settled = () => {
        if (active && version() === current) reload();
      };
      void locked(async () => settled()).catch(settled);
    };
    const storage = (event: StorageEvent) => {
      if (event.key === versionKey || event.key === null) inspect();
    };
    globalThis.addEventListener("storage", storage);
    globalThis.addEventListener("focus", inspect);
    globalThis.addEventListener("pageshow", inspect);
    return () => {
      active = false;
      globalThis.removeEventListener("storage", storage);
      globalThis.removeEventListener("focus", inspect);
      globalThis.removeEventListener("pageshow", inspect);
    };
  }
  return { sessionFetch, recover, changed, version, assertCurrent, subscribeToChanges };
}
